import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { Waypoint } from 'react-waypoint';
import Button from '@atlaskit/button';
import Tabs, { Tab, TabList, useTabPanel } from '@atlaskit/tabs';
import { triggerRequestTabClicked, useCreateFireAnalyticsFromTrigger } from '../../common/utils/analytics';
import { isMarkAllAsReadExperimentEnabled, isProductFilterExperimentEnabled, isStickyHeaderExperimentEnabled } from '../../common/utils/experiments';
import { useLocalStorage as useLocalStorageDI } from '../../common/utils/use-local-storage';
import { ErrorBoundarySuspense, messages } from '../../index';
import { Header } from './header';
import { LoadMoreWaypoint } from './load-more-waypoint';
import { ProductButton } from './product-filters';
import { ProductWrapper, TabListStickyHeader, TabWrapper, WaypointWrapper } from './styled';
var LazyInboxAnnouncementBanner = /*#__PURE__*/React.lazy(function () {
  return import( /* webpackChunkName: "@atlaskit-internal_notification-common/InboxAnnouncementBanner" */
  './inbox-announcement-banner');
});
var HAS_DISMISSED_INBOX_ANNOUNCEMENT_LOCAL_STORAGE_KEY = 'notifications-list.hasDismissedInboxAnnouncementBanner';
export default function NotificationsLayout(_ref) {
  var _di$useLocalStorage;
  var fetchData = _ref.fetchData,
    listSize = _ref.listSize,
    fetchState = _ref.fetchState,
    canLoadMore = _ref.canLoadMore,
    notificationFilters = _ref.notificationFilters,
    NotificationListComponent = _ref.NotificationListComponent,
    ProductDropdownComponent = _ref.ProductDropdownComponent,
    isMarkAllAsReadVisible = _ref.isMarkAllAsReadVisible,
    handleMarkAllNotificationsRead = _ref.handleMarkAllNotificationsRead,
    notificationTabs = _ref.notificationTabs,
    productFilters = _ref.productFilters,
    headerStickyTop = _ref.headerStickyTop,
    _ref$spacing = _ref.spacing,
    spacing = _ref$spacing === void 0 ? 'comfortable' : _ref$spacing,
    toggleGroupedNotificationsView = _ref.toggleGroupedNotificationsView,
    isGroupedNotificationsViewOn = _ref.isGroupedNotificationsViewOn,
    HeaderFullPageButtonComponent = _ref.HeaderFullPageButtonComponent,
    _ref$enableInboxAnnou = _ref.enableInboxAnnouncementBanner,
    enableInboxAnnouncementBanner = _ref$enableInboxAnnou === void 0 ? false : _ref$enableInboxAnnou,
    _ref$enableProductFil = _ref.enableProductFiltering,
    enableProductFiltering = _ref$enableProductFil === void 0 ? false : _ref$enableProductFil,
    _ref$fullPageNotifica = _ref.fullPageNotificationBranding,
    fullPageNotificationBranding = _ref$fullPageNotifica === void 0 ? 'full-page' : _ref$fullPageNotifica,
    di = _ref.di;
  var notifDrawerRef = useRef(null);
  var useLocalStorage = (_di$useLocalStorage = di === null || di === void 0 ? void 0 : di.useLocalStorage) !== null && _di$useLocalStorage !== void 0 ? _di$useLocalStorage : useLocalStorageDI;
  var handleRequestTabAnalytics = useCreateFireAnalyticsFromTrigger(triggerRequestTabClicked);
  var intl = useIntl();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    headerWaypointHidden = _useState2[0],
    setHeaderWaypointHidden = _useState2[1];
  var _useState3 = useState(messages.timeGroupingToday),
    _useState4 = _slicedToArray(_useState3, 2),
    groupingHeader = _useState4[0],
    setGroupingHeader = _useState4[1];
  var _useLocalStorage = useLocalStorage(HAS_DISMISSED_INBOX_ANNOUNCEMENT_LOCAL_STORAGE_KEY, false),
    _useLocalStorage2 = _slicedToArray(_useLocalStorage, 2),
    hasDismissedInboxAnnouncementBanner = _useLocalStorage2[0],
    setHasDismissedInboxAnnouncementBanner = _useLocalStorage2[1];
  var handleInboxAnnouncementBannerCloseButtonClicked = useCallback(function () {
    setHasDismissedInboxAnnouncementBanner(true);
  }, [setHasDismissedInboxAnnouncementBanner]);
  var shouldShowInboxAnnouncementBanner = enableInboxAnnouncementBanner && !hasDismissedInboxAnnouncementBanner;
  var tabStickyHeaderEnabled = isStickyHeaderExperimentEnabled();
  var updateGroupingHeaders = function updateGroupingHeaders(message) {
    setGroupingHeader(message);
  };
  var formatMessage = intl.formatMessage;
  var tabData = useMemo(function () {
    return notificationTabs.map(function (tab) {
      return {
        label: formatMessage(tab.label),
        category: tab.key,
        content: /*#__PURE__*/React.createElement(NotificationListComponent, {
          key: "category_".concat(tab.key),
          updateGroupingHeaders: updateGroupingHeaders
        })
      };
    });
  }, [formatMessage, NotificationListComponent, notificationTabs]);
  var selectedTab = notificationTabs.findIndex(function (tab) {
    return tab.key === notificationFilters.filters.categoryFilter;
  });
  var handleTabSelect = function handleTabSelect(selected) {
    notificationFilters.handleSetCategoryFilter(selected.category);
    handleRequestTabAnalytics(selected.category);
    if (notifDrawerRef.current) {
      notifDrawerRef.current.scrollIntoView({
        behavior: 'instant',
        block: 'start'
      });
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    ref: notifDrawerRef
  }, /*#__PURE__*/React.createElement(Header, {
    headerStickyTop: headerStickyTop,
    isMarkAllAsReadVisible: isMarkAllAsReadVisible,
    handleMarkAllNotificationsRead: handleMarkAllNotificationsRead,
    toggleReadStateFilter: notificationFilters.toggleReadStateFilter,
    readStateFilter: notificationFilters.filters.readStateFilter,
    requestCategory: notificationFilters.filters.categoryFilter,
    groupingHeader: intl.formatMessage(groupingHeader),
    isSticky: headerWaypointHidden && !tabStickyHeaderEnabled,
    spacing: spacing,
    toggleGroupedNotificationsView: toggleGroupedNotificationsView,
    isGroupedNotificationsViewOn: isGroupedNotificationsViewOn,
    fullPageNotificationBranding: fullPageNotificationBranding,
    shouldPlayInboxAnnouncementAnimation: shouldShowInboxAnnouncementBanner,
    FullPageButton: HeaderFullPageButtonComponent
  }), shouldShowInboxAnnouncementBanner && /*#__PURE__*/React.createElement(ErrorBoundarySuspense, {
    loadingFallback: /*#__PURE__*/React.createElement("div", {
      "data-testid": "inbox-announcement-banner-loading"
    }),
    errorFallback: /*#__PURE__*/React.createElement("div", null),
    subjectId: "inbox-announcement-banner-chunk"
  }, /*#__PURE__*/React.createElement(LazyInboxAnnouncementBanner, {
    onCloseButtonClicked: handleInboxAnnouncementBannerCloseButtonClicked
  })), /*#__PURE__*/React.createElement(Waypoint, {
    onEnter: function onEnter() {
      return setHeaderWaypointHidden(false);
    },
    onLeave: function onLeave() {
      return setHeaderWaypointHidden(true);
    }
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Tabs, {
    onChange: function onChange(index) {
      var selectedTab = tabData[index];
      handleTabSelect(selectedTab);
    },
    selected: selectedTab,
    id: "1wvhohfusvxqah2l3vkx8e",
    testId: "category-filter-tab",
    shouldUnmountTabPanelOnChange: true
  }, /*#__PURE__*/React.createElement(TabListStickyHeader, {
    top: headerStickyTop,
    isSticky: headerWaypointHidden && tabStickyHeaderEnabled,
    spacing: spacing,
    "data-testid": "notifications-tabs-sticky-header-".concat(headerWaypointHidden && tabStickyHeaderEnabled ? 'enabled' : 'disabled')
  }, /*#__PURE__*/React.createElement(TabList, null, tabData.map(function (tab) {
    return /*#__PURE__*/React.createElement(Tab, {
      testId: "notification-list-layout-".concat(tab.label),
      key: tab.category
    }, tab.label);
  }), ProductDropdownComponent), isMarkAllAsReadVisible && headerWaypointHidden && tabStickyHeaderEnabled && !isMarkAllAsReadExperimentEnabled() && /*#__PURE__*/React.createElement(Button, {
    testId: "sticky-header-mark-all-button",
    spacing: "none",
    appearance: "subtle-link",
    onClick: handleMarkAllNotificationsRead
  }, /*#__PURE__*/React.createElement(FormattedMessage, messages.markAllAsRead))), tabData.map(function (tab) {
    return /*#__PURE__*/React.createElement(TabWrapper, {
      spacing: spacing
    }, isProductFilterExperimentEnabled() && enableProductFiltering && /*#__PURE__*/React.createElement(ProductWrapper, null, productFilters.map(function (product) {
      return /*#__PURE__*/React.createElement(ProductButton, {
        key: "filter-".concat(product.key),
        product: product,
        isSelected: notificationFilters.filters.productFilter === product.key,
        notificationFilters: notificationFilters
      });
    })), /*#__PURE__*/React.createElement(NoFocusTabPanel, {
      key: tab.label
    }, tab.content));
  }))), /*#__PURE__*/React.createElement(WaypointWrapper, null, /*#__PURE__*/React.createElement(LoadMoreWaypoint, {
    listSize: listSize,
    fetchState: fetchState,
    canLoadMore: canLoadMore,
    fetchData: fetchData,
    readStateFilter: notificationFilters.filters.readStateFilter
  })));
}

/**
 * Overrides default TabPanel from @atlaskit/tabs and sets tabIndex = -1
 * Notifications are already focusable so the panel does not need focus.
 */
function NoFocusTabPanel(_ref2) {
  var children = _ref2.children;
  var tabPanelAttributes = useTabPanel();
  return /*#__PURE__*/React.createElement("div", _extends({}, tabPanelAttributes, {
    tabIndex: -1
  }), children);
}